<template>
  <BaseLayout>
    <template v-slot:top_buttons>
      <div class="demo-inline-spacing">
        <button
            type="button"
            class="btn btn-primary"
            :disabled="csvLoading"
            @click="downloadCsv">
          <span v-if="csvLoading" class="spinner-border spinner-border-sm me-2" role="status"></span>
          Скачать CSV
        </button>
      </div>
    </template>

    <LoadingBlock :loading="loading">
      <!-- Task info + progress -->
      <div v-if="info" class="card mb-4">
        <div class="card-body">
          <div class="d-flex justify-content-between align-items-center mb-2">
            <h5 class="card-title mb-0">{{ info.name }}</h5>
            <span class="badge" :class="resultBadgeClass">{{ resultDescription }}</span>
          </div>

          <div class="progress mb-2" style="height: 20px;">
            <div
                class="progress-bar"
                :class="{'progress-bar-striped progress-bar-animated': isInProgress}"
                role="progressbar"
                :style="{width: progress + '%'}"
                :aria-valuenow="progress"
                aria-valuemin="0"
                aria-valuemax="100">
              {{ progress }} %
            </div>
          </div>

          <p v-if="info.task_info" class="mb-2 text-muted">{{ info.task_info }}</p>

          <div class="text-muted small">
            <span class="me-3">Начало: {{ formatDate(info.start_time) || '—' }}</span>
            <span>Завершение: {{ formatDate(info.end_time) || '—' }}</span>
          </div>
        </div>
      </div>

      <!-- Summary badges -->
      <div v-if="hasSummary" class="mb-4">
        <span class="badge bg-label-primary me-2 fs-6">Всего: {{ summary.total ?? 0 }}</span>
        <span class="badge bg-label-success me-2 fs-6">Успешно: {{ summary.success ?? 0 }}</span>
        <span class="badge bg-label-warning me-2 fs-6">Пропущено: {{ summary.skipped ?? 0 }}</span>
        <span class="badge bg-label-danger fs-6">Ошибки: {{ summary.error ?? 0 }}</span>
      </div>

      <!-- Report -->
      <div class="card">
        <div class="card-header d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-2">
          <h5 class="mb-0">Отчёт</h5>
          <div class="d-flex align-items-center gap-2">
            <button
                type="button"
                class="btn btn-sm btn-outline-primary text-nowrap"
                :disabled="reportLoading"
                @click="refresh">
              Обновить
            </button>
            <template v-if="!isCertificateLinks">
            <label class="form-label mb-0 text-nowrap">Статус:</label>
            <select class="form-select form-select-sm" v-model="statusFilter" @change="onFilterChange">
              <option value="">Все</option>
              <option value="success">Успешно</option>
              <option value="skipped">Пропущено</option>
              <option value="error">Ошибка</option>
            </select>
            </template>
          </div>
        </div>

        <ul v-if="isCertificateLinks" class="nav nav-tabs px-4">
          <li v-for="tab in certificateTabs" :key="tab.value" class="nav-item">
            <a href="#" class="nav-link" :class="{ active: statusFilter === tab.value }"
               @click.prevent="setStatus(tab.value)">
              {{ tab.label }}
              <span v-if="tab.value && summary[tab.value] !== undefined" class="badge rounded-pill bg-label-secondary ms-1">
                {{ summary[tab.value] }}
              </span>
            </a>
          </li>
        </ul>

        <LoadingBlock :loading="reportLoading">
          <div class="card-body">
            <div v-if="!reportItems.length && !reportLoading" class="text-muted">
              Записи отчёта отсутствуют
            </div>
          </div>

          <div v-if="reportItems.length" class="table-responsive text-nowrap">
            <table class="table">
              <thead>
              <tr>
                <th>Время</th>
                <th>Идентификатор</th>
                <th>Тип</th>
                <th>Статус</th>
                <th>Сообщение</th>
              </tr>
              </thead>
              <tbody>
              <tr v-for="(row, index) in reportItems" :key="index">
                <td>{{ formatDate(row.created_at) || '—' }}</td>
                <td>
                  <a v-if="isUrl(row.reference)" :href="row.reference" target="_blank" rel="noopener noreferrer"
                     class="text-wrap text-break d-inline-block reference-link">{{ row.reference }}</a>
                  <span v-else>{{ row.reference || row.entity_id || '—' }}</span>
                  <div v-if="certificateLink(row)" class="small">
                    <router-link :to="certificateLink(row)">
                      {{ row.name ? 'Сертификат ' + row.name : 'Открыть сертификат' }}
                    </router-link>
                  </div>
                  <div v-else-if="row.name" class="small text-muted">{{ row.name }}</div>
                </td>
                <td>{{ row.entity_type || '—' }}</td>
                <td>
                  <span class="badge" :class="rowStatusClass(row.status)">
                    {{ rowStatusLabel(row.status) }}
                  </span>
                </td>
                <td class="text-wrap">{{ row.message || '—' }}</td>
              </tr>
              </tbody>
            </table>

            <BasePagination :info="paginationInfo" @changePage="changePage"/>
          </div>
        </LoadingBlock>
      </div>
    </LoadingBlock>
  </BaseLayout>
</template>

<script>
import BaseLayout from '@/layouts/Base.vue'
import {PageInfo} from "@/data/page";
import {user} from "@/data/user";
import BasePagination from "@/components/list/Pagination.vue"
import LoadingBlock from "@/components/page/LoadingBlock.vue";
import {Message, Messages} from "@/data/messages";
import {getInfo, getReport, downloadReportCsv} from "@/api/task";
import {CERTIFICATE_COPY_TASK, isCertificateLinksTask} from "@/data/certificate";

const POLL_INTERVAL = 3000

const RESULT_DESCRIPTIONS = {
  'in_progress': 'В процессе',
  'success': 'Успешно выполнена',
  'failure': 'Произошла ошибка',
}

const ROW_STATUS_LABELS = {
  'success': 'Успешно',
  'skipped': 'Пропущено',
  'error': 'Ошибка',
}

export default {
  name: 'TaskDetailView',
  components: {LoadingBlock, BaseLayout, BasePagination},
  data() {
    return {
      info: null,
      summary: {},
      loading: true,
      reportLoading: false,
      csvLoading: false,
      reportItems: [],
      paginationInfo: {},
      statusFilter: '',
      pollTimer: null,
    }
  },
  computed: {
    taskId() {
      return this.$route.params.id
    },
    isInProgress() {
      return this.info?.task_result === 'in_progress'
    },
    progress() {
      return this.info?.task_progress ?? 0
    },
    resultDescription() {
      return RESULT_DESCRIPTIONS[this.info?.task_result] || this.info?.task_result || ''
    },
    resultBadgeClass() {
      const map = {
        'in_progress': 'bg-label-info',
        'success': 'bg-label-success',
        'failure': 'bg-label-danger',
      }
      return map[this.info?.task_result] || 'bg-label-secondary'
    },
    isCertificateLinks() {
      return isCertificateLinksTask(this.info)
    },
    certificateTabs() {
      const isCopy = (this.info?.task_type || this.info?.type) === CERTIFICATE_COPY_TASK
          || (this.info?.name || '').startsWith('Копирование сертификатов')
      return [
        {value: '', label: 'Все'},
        {value: 'success', label: isCopy ? 'Скопировано' : 'Загружено'},
        {value: 'skipped', label: 'Пропущено'},
        {value: 'error', label: 'Ошибки'},
      ]
    },
    hasSummary() {
      return this.summary && Object.keys(this.summary).length > 0
    },
  },
  methods: {
    async loadInfo() {
      const result = await getInfo(this.taskId)
      this.info = result.data
      this.setPageInfo()
    },
    async loadReport() {
      this.reportLoading = true
      try {
        const query = {...this.$route.query}
        if (this.statusFilter) {
          query.status = this.statusFilter
        } else {
          delete query.status
        }
        const result = await getReport(this.taskId, query)
        this.reportItems = result.data.items
        this.paginationInfo = {
          total: result.data.total,
          pages: result.data.pages,
          page: result.data.page,
          size: result.data.size,
        }
        if (result.data.summary) {
          this.summary = result.data.summary
        }
      } finally {
        this.reportLoading = false
      }
    },
    startPolling() {
      this.stopPolling()
      this.pollTimer = setInterval(async () => {
        await this.loadInfo()
        if (!this.isInProgress) {
          this.stopPolling()
          await this.loadReport()
        }
      }, POLL_INTERVAL)
    },
    stopPolling() {
      if (this.pollTimer) {
        clearInterval(this.pollTimer)
        this.pollTimer = null
      }
    },
    async refresh() {
      // Итоги некоторых задач (например, выгрузки на WB) дописываются в отчёт уже после её завершения
      await this.loadInfo()
      await this.loadReport()
    },
    async downloadCsv() {
      this.csvLoading = true
      const message = new Message()
      try {
        await downloadReportCsv(this.taskId)
      } catch (e) {
        message.type = 'failure'
        message.text = e.message || 'Не удалось скачать CSV'
        Messages.list.push(message)
      } finally {
        this.csvLoading = false
      }
    },
    async onFilterChange() {
      const query = {...this.$route.query}
      delete query.page
      if (this.statusFilter) {
        query.status = this.statusFilter
      } else {
        delete query.status
      }
      await this.$router.push({path: this.$route.path, query})
    },
    async changePage(page) {
      await this.$router.push({
        path: this.$route.path,
        query: {...this.$route.query, page},
      })
    },
    async setStatus(status) {
      this.statusFilter = status
      await this.onFilterChange()
    },
    isUrl(value) {
      return typeof value === 'string' && /^https?:\/\//i.test(value)
    },
    certificateLink(row) {
      if (!this.isCertificateLinks || row.status !== 'success' || !row.entity_id) {
        return null
      }
      return {name: 'certificate-detail', params: {id: row.entity_id}}
    },
    rowStatusLabel(status) {
      return ROW_STATUS_LABELS[status] || status || '—'
    },
    rowStatusClass(status) {
      const map = {
        'success': 'bg-label-success',
        'skipped': 'bg-label-warning',
        'error': 'bg-label-danger',
      }
      return map[status] || 'bg-label-secondary'
    },
    formatDate(isoString) {
      if (!isoString) {
        return ''
      }
      const date = new Date(isoString)
      const day = String(date.getDate()).padStart(2, '0')
      const month = String(date.getMonth() + 1).padStart(2, '0')
      const year = date.getFullYear()
      const hours = String(date.getHours()).padStart(2, '0')
      const minutes = String(date.getMinutes()).padStart(2, '0')
      const seconds = String(date.getSeconds()).padStart(2, '0')

      return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`
    },
    setPageInfo() {
      PageInfo.title = this.info?.name || 'Задача'
      PageInfo.breadcrumbs = [
        {title: 'Главная', link: '/'},
        {title: 'Задачи', link: '/tasks'},
        {title: PageInfo.title},
      ]
    },
    setFilterFromQuery() {
      this.statusFilter = this.$route.query.status || ''
    },
    async load() {
      this.loading = true
      this.setFilterFromQuery()
      await this.loadInfo()
      await this.loadReport()
      this.loading = false
      if (this.isInProgress) {
        this.startPolling()
      }
    },
  },
  async mounted() {
    await user.getUserBase()
    await this.load()
  },
  watch: {
    '$route.query'() {
      this.setFilterFromQuery()
      this.loadReport()
    },
    '$route.params.id'() {
      this.stopPolling()
      this.load()
    },
  },
  beforeUnmount() {
    this.stopPolling()
  },
}
</script>

<style scoped>
.reference-link {
  max-width: 420px;
}
</style>
